/**
 * React Query hooks + types for the HQ Content editor console.
 *
 * Backend: api#343 + migration `0096`. All routes live under `/v1/content/*`
 * and require the `stores.content` accessSlug (spec 00_access-binary-grants.md,
 * 2026-08-09; renamed from the old content:admin capability). The tool powers
 * HQ editing of items (Promotions / Resources / Marketing / Recognition
 * submissions / etc.) that the Sweet Spot store site renders read-only.
 *
 * Mirrors the react-query patterns in `useStores.ts` / `useAccess.ts`.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from './api';
import { API_BASE } from './apiBase';

export const CONTENT_API_BASE = `${API_BASE}/v1/content`;

export function hasContentAdmin(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('stores.content');
}

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export type ContentArea =
  | 'training'
  | 'promotions'
  | 'marketing'
  | 'recognition'
  | 'ideas'
  | 'notes';

export const CONTENT_AREAS: ContentArea[] = [
  'training',
  'promotions',
  'marketing',
  'recognition',
  'ideas',
  'notes',
];

// Display label only. The key stays `training` because it is the stored
// ContentArea value (api payloads, database rows, URLs); renaming it would
// orphan every item already published. Sweet Spot made the same call when it
// renamed its Training nav entry to Resources.
export const CONTENT_AREA_LABELS: Record<ContentArea, string> = {
  training: 'Resources',
  promotions: 'Promotions',
  marketing: 'Marketing',
  recognition: 'Recognition',
  ideas: 'Ideas',
  notes: 'Notes',
};

export type ContentStatus = 'draft' | 'submitted' | 'published' | 'archived';

// Status filter order: most-actionable first (Submitted -> Draft -> Published
// -> Archived) so curators see the queue before authored drafts, per issue.
export const CONTENT_STATUS_FILTER_ORDER: ContentStatus[] = [
  'submitted',
  'draft',
  'published',
  'archived',
];

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  published: 'Published',
  archived: 'Archived',
};

export type ContentAttachment = {
  id: number;
  filename: string;
  content_type: string;
};

// The `fields` blob is per-area. Promotions and marketing carry structured
// fields; the rest are free-form and left as `Record<string, unknown>`.
export type PromoFields = {
  start_date?: string | null;
  end_date?: string | null;
  skus?: string[];
  target_stores?: string[];
};

export type ContentItem = {
  id: number;
  area: ContentArea;
  title: string;
  body_html: string | null;
  fields: PromoFields & Record<string, unknown>;
  status: ContentStatus;
  store_no: number | string | null;
  owner_email: string | null;
  review_date: string | null;
  published_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  attachments: ContentAttachment[];
};

export type ContentListFilters = {
  area?: ContentArea;
  status?: ContentStatus;
  q?: string;
};

// --------------------------------------------------------------------------
// Local helpers (pure)
// --------------------------------------------------------------------------

// A review is past-due when the review_date is a valid YYYY-MM-DD (or ISO) in
// the past relative to `today` (also a YYYY-MM-DD in the caller's timezone —
// the list page uses the browser's local today). Undefined / null / malformed
// returns false so the list never mis-flags a missing review.
export function isReviewPastDue(
  review: string | null | undefined,
  today: string,
): boolean {
  if (!review) return false;
  const s = String(review).trim();
  if (!s) return false;
  // Compare on the date portion only. YYYY-MM-DD strings sort lexicographically.
  const date = s.length >= 10 ? s.slice(0, 10) : s;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return date < today;
}

export function localTodayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function contentListQs(filters: ContentListFilters): string {
  const params = new URLSearchParams();
  if (filters.area) params.set('area', filters.area);
  if (filters.status) params.set('status', filters.status);
  if (filters.q && filters.q.trim()) params.set('q', filters.q.trim());
  const s = params.toString();
  return s ? `?${s}` : '';
}

// --------------------------------------------------------------------------
// Query hooks
// --------------------------------------------------------------------------

type ItemsResponse = { items: ContentItem[] };

export function useContentItems(filters: ContentListFilters) {
  return useQuery<ItemsResponse, Error>({
    queryKey: ['content', 'items', filters],
    queryFn: () => apiGet<ItemsResponse>(`${CONTENT_API_BASE}/items${contentListQs(filters)}`),
    staleTime: 15_000,
    retry: false,
  });
}

export function useContentItem(id: number | 'new' | undefined): UseQueryResult<
  { item: ContentItem },
  Error
> {
  const enabled = typeof id === 'number' && Number.isFinite(id);
  return useQuery<{ item: ContentItem }, Error>({
    queryKey: ['content', 'item', id ?? 0],
    queryFn: () =>
      apiGet<{ item: ContentItem }>(`${CONTENT_API_BASE}/items/${id}`),
    enabled,
    staleTime: 10_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export type UpsertContentBody = {
  area?: ContentArea;
  title?: string;
  body_html?: string | null;
  fields?: PromoFields & Record<string, unknown>;
  owner_email?: string | null;
  review_date?: string | null;
};

export function useCreateContentItem() {
  const qc = useQueryClient();
  return useMutation<{ item: ContentItem }, Error, UpsertContentBody & { area: ContentArea; title: string }>({
    mutationFn: (body) =>
      apiPost<{ item: ContentItem }>(`${CONTENT_API_BASE}/items`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['content', 'items'] });
    },
  });
}

export function useUpdateContentItem(id: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ item: ContentItem }, Error, UpsertContentBody>({
    mutationFn: (body) =>
      apiPatch<{ item: ContentItem }>(`${CONTENT_API_BASE}/items/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['content', 'items'] });
      if (id != null) {
        void qc.invalidateQueries({ queryKey: ['content', 'item', id] });
      }
    },
  });
}

// Transitions. Each is a POST with an empty body; the api owns the state
// machine (draft/submitted -> published, submitted -> archived on reject, etc.).
export type ContentTransition = 'publish' | 'archive' | 'approve' | 'reject';

export function useContentTransition(id: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ item: ContentItem }, Error, ContentTransition>({
    mutationFn: (action) =>
      apiPost<{ item: ContentItem }>(`${CONTENT_API_BASE}/items/${id}/${action}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['content', 'items'] });
      if (id != null) {
        void qc.invalidateQueries({ queryKey: ['content', 'item', id] });
      }
    },
  });
}

// --------------------------------------------------------------------------
// Attachments
// --------------------------------------------------------------------------

export type RequestAttachmentBody = { filename: string; content_type: string };

export type RequestAttachmentResponse = {
  attachment_id: number;
  key: string;
  upload_url: string;
};

// Two-step attachment flow, mirroring the store PhotoCapture pattern:
//   1. POST /items/{id}/attachments { filename, content_type } -> presign
//   2. Browser PUTs bytes directly to upload_url.
export function useRequestAttachmentUpload(id: number | undefined) {
  const qc = useQueryClient();
  return useMutation<RequestAttachmentResponse, Error, RequestAttachmentBody>({
    mutationFn: (body) =>
      apiPost<RequestAttachmentResponse>(
        `${CONTENT_API_BASE}/items/${id}/attachments`,
        body,
      ),
    onSuccess: () => {
      if (id != null) {
        void qc.invalidateQueries({ queryKey: ['content', 'item', id] });
      }
    },
  });
}

export type AttachmentUrlResponse = {
  url: string;
  filename: string;
  content_type: string;
};

// Like useRequestAttachmentUpload but takes itemId as a mutation variable instead
// of a hook argument, so the caller can use a dynamically-created item id (e.g.
// after auto-creating a draft on the new-item screen before the first upload).
export function useRequestAttachmentUploadDynamic() {
  const qc = useQueryClient();
  return useMutation<
    RequestAttachmentResponse,
    Error,
    RequestAttachmentBody & { itemId: number }
  >({
    mutationFn: ({ itemId, ...body }) =>
      apiPost<RequestAttachmentResponse>(
        `${CONTENT_API_BASE}/items/${itemId}/attachments`,
        body,
      ),
    onSuccess: (_, { itemId }) => {
      void qc.invalidateQueries({ queryKey: ['content', 'item', itemId] });
    },
  });
}

export function useAttachmentUrl(itemId: number | undefined, attId: number | undefined) {
  return useQuery<AttachmentUrlResponse, Error>({
    queryKey: ['content', 'attachment-url', itemId ?? 0, attId ?? 0],
    queryFn: () =>
      apiGet<AttachmentUrlResponse>(
        `${CONTENT_API_BASE}/items/${itemId}/attachments/${attId}`,
      ),
    enabled: itemId != null && attId != null,
    // Presigned URLs are short-lived; refetch on demand.
    staleTime: 5 * 60_000,
    retry: false,
  });
}
